import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CreditCard, Loader2, Percent, ReceiptText, RefreshCw, Sparkles, TrendingUp, Users, Wallet } from 'lucide-react'
import toast from 'react-hot-toast'
import { feesApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import FeeAnalyticsPage from './FeeAnalyticsPage.jsx'
import FeeStructuresPage from './FeeStructuresPage.jsx'
import { formatCurrency } from '../utils/fee.js'

const tabs = [
  { value: 'overview', label: 'Overview' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'structures', label: 'Structures' },
]

export default function FeeManagementPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [summary, setSummary] = useState(null)
  const [defaulters, setDefaulters] = useState([])
  const [structures, setStructures] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchOverview = async () => {
    try {
      setLoading(true)
      const [summaryRes, defaultersRes, structuresRes] = await Promise.allSettled([
        feesApi.getSummary(),
        feesApi.getDefaulters(),
        feesApi.getStructures(),
      ])
      setSummary(summaryRes.status === 'fulfilled' ? summaryRes.value : null)
      setDefaulters(defaultersRes.status === 'fulfilled' && Array.isArray(defaultersRes.value) ? defaultersRes.value : [])
      setStructures(structuresRes.status === 'fulfilled' && Array.isArray(structuresRes.value) ? structuresRes.value : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee overview')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'overview') fetchOverview()
  }, [activeTab])

  const statCards = useMemo(() => [
    {
      label: 'Total Demand',
      value: formatCurrency(summary?.totalDemand || 0),
      icon: Wallet,
      tone: 'bg-primary-50 text-primary-700',
    },
    {
      label: 'Collected',
      value: formatCurrency(summary?.totalCollected || 0),
      icon: TrendingUp,
      tone: 'bg-green-50 text-green-700',
    },
    {
      label: 'Outstanding',
      value: formatCurrency(summary?.totalPending || 0),
      icon: AlertCircle,
      tone: 'bg-red-50 text-red-600',
    },
    {
      label: 'Fee Structures',
      value: structures.length,
      icon: ReceiptText,
      tone: 'bg-blue-50 text-blue-700',
    },
  ], [summary, structures.length])

  if (activeTab === 'analytics') {
    return (
      <div className="space-y-5">
        <ManagementTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <FeeAnalyticsPage />
      </div>
    )
  }

  if (activeTab === 'structures') {
    return (
      <div className="space-y-5">
        <ManagementTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <FeeStructuresPage />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Management"
        subtitle="RN-style admin command center for fee overview, analytics, and structures."
        chips={[
          loading ? 'Loading overview' : `${summary?.totalStudents || 0} fee accounts`,
          loading ? 'Loading defaulters' : `${defaulters.length} defaulters`,
          loading ? 'Loading structures' : `${structures.length} structures`,
        ]}
        actions={
          <>
            <button onClick={fetchOverview} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={() => navigate('/fees/collection')} className="btn-primary">
              <CreditCard className="w-4 h-4" /> Collect Fee
            </button>
          </>
        }
      />

      <ManagementTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((card) => {
              const Icon = card.icon
              return (
                <div key={card.label} className="card p-5">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card.tone}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 mt-4">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
              )
            })}
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_420px]">
            <div className="card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                  <p className="text-sm text-gray-500 mt-1">The same admin shortcuts exposed in the RN management screen.</p>
                </div>
                <Sparkles className="w-5 h-5 text-gray-300" />
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {[
                  { label: 'Fee Accounts', desc: 'Review generated accounts', icon: Users, to: '/fees/accounts' },
                  { label: 'Defaulters', desc: 'Follow up pending dues', icon: AlertCircle, to: '/fees/defaulters' },
                  { label: 'Collect Fee', desc: 'Record offline payment', icon: CreditCard, to: '/fees/collection' },
                  { label: 'Apply Discount / Fine', desc: 'Adjust fee balances', icon: Percent, to: '/fees/discounts' },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 text-left transition-colors hover:border-primary-200 hover:bg-primary-50"
                    >
                      <Icon className="w-5 h-5 text-primary-600" />
                      <p className="mt-3 font-semibold text-gray-900">{item.label}</p>
                      <p className="mt-1 text-sm text-gray-500">{item.desc}</p>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Top Defaulters</h3>
                  <p className="text-sm text-gray-500 mt-1">Highest outstanding accounts right now.</p>
                </div>
                <button onClick={() => navigate('/fees/defaulters')} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                  View all
                </button>
              </div>
              <div className="mt-5 space-y-3">
                {defaulters.length === 0 ? (
                  <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-6 text-center">
                    <p className="text-sm font-semibold text-green-700">No defaulters right now</p>
                    <p className="text-xs text-green-600 mt-1">Collection status looks healthy.</p>
                  </div>
                ) : (
                  defaulters.slice(0, 5).map((item) => {
                    const name = [item.studentId?.firstName, item.studentId?.lastName].filter(Boolean).join(' ').trim() || 'Student'
                    return (
                      <button
                        key={item._id || item.id}
                        onClick={() => navigate(`/fees/accounts/${item._id || item.id}`)}
                        className="w-full rounded-2xl border border-gray-100 bg-white px-4 py-3 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-gray-900">{name}</p>
                            <p className="mt-1 truncate text-xs text-gray-500">
                              {item.classId?.name || 'No class'}{item.classId?.section ? ` - ${item.classId.section}` : ''}
                              {item.studentId?.admissionNo ? ` • ${item.studentId.admissionNo}` : ''}
                            </p>
                          </div>
                          <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
                            {formatCurrency(item.overdueAmount || item.outstanding || 0)}
                          </span>
                        </div>
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function ManagementTabs({ activeTab, setActiveTab }) {
  return (
    <div className="card p-2">
      <div className="grid gap-2 sm:grid-cols-3">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === tab.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  )
}
